import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArticleIcon from '@mui/icons-material/Article';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { getCourseById, createCourse, updateCourse, uploadCoursePictures, deleteCourseImage } from '../../api/TrainerAPI';
import './styles/CourseEditor.css';

const CourseEditor = () => {
    const navigate = useNavigate();
    const { personId, courseId } = useParams();
    const location = useLocation();
    
    const isEditMode = !!courseId;
    
    const [course, setCourse] = useState({
        title: '',
        description: '',
        modules: []
    });
    const [originalCourse, setOriginalCourse] = useState(null);
    const [isLoading, setIsLoading] = useState(isEditMode);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [validationError, setValidationError] = useState('');
    
    const [expandedModules, setExpandedModules] = useState({});
    const [expandedChapters, setExpandedChapters] = useState({});
    const [expandedLessons, setExpandedLessons] = useState({});
    
    // Track pending image uploads (file objects by block ID)
    const pendingImageUploads = useRef({});
    // Track deleted image IDs for cleanup
    const deletedImageIds = useRef([]);
    
    const imageInputRefs = useRef({});
    const videoInputRefs = useRef({});
    const blockImageInputRefs = useRef({});

    useEffect(() => {
        if (isEditMode && personId && courseId) {
            loadCourse();
        }
    }, [personId, courseId, isEditMode]);

    const loadCourse = async () => {
        setIsLoading(true);
        setError('');
        try {
            const courseData = await getCourseById(personId, courseId);
            if (courseData) {
                setCourse(courseData);
                setOriginalCourse(JSON.parse(JSON.stringify(courseData)));
            } else {
                setError('Course not found');
            }
        } catch (err) {
            setError('Failed to load course');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setValidationError('');
        
        if (!course.title.trim()) {
            setValidationError('Course title is required');
            return;
        }
        if (!course.description.trim()) {
            setValidationError('Course description is required');
            return;
        }

        setIsSaving(true);
        try {
            // For new courses, create first to get courseId
            let targetCourseId = courseId;
            if (!isEditMode) {
                targetCourseId = await createCourse(personId, course);
            }

            // Collect all pending image uploads
            const pendingUploads = Object.entries(pendingImageUploads.current);
            
            if (pendingUploads.length > 0) {
                const fileItems = pendingUploads.map(([imageId, file]) => ({
                    file,
                    imageId
                }));

                // Upload all images
                const uploadedImages = await uploadCoursePictures(fileItems, personId, targetCourseId);

                // Create a map of imageId -> url
                const urlMap = {};
                uploadedImages.forEach(({ imageId, url }) => {
                    urlMap[imageId] = url;
                });

                // Update course content blocks with actual URLs
                const updatedCourse = { ...course };
                updatedCourse.modules = updatedCourse.modules.map(module => ({
                    ...module,
                    chapters: (module.chapters || []).map(chapter => ({
                        ...chapter,
                        lessons: (chapter.lessons || []).map(lesson => ({
                            ...lesson,
                            contentBlocks: (lesson.contentBlocks || []).map(block => {
                                if (block.type === 'image' && block._pendingFile && urlMap[block.id]) {
                                    // Replace pending with actual URL
                                    const { _pendingFile, _localPreview, ...rest } = block;
                                    return {
                                        ...rest,
                                        url: urlMap[block.id]
                                    };
                                }
                                return block;
                            })
                        }))
                    }))
                }));

                // Update the course with uploaded URLs
                if (isEditMode) {
                    await updateCourse(personId, targetCourseId, updatedCourse);
                } else {
                    await updateCourse(personId, targetCourseId, updatedCourse);
                }
            } else if (isEditMode) {
                await updateCourse(personId, targetCourseId, course);
            }

            // Delete removed images from Firebase Storage
            if (deletedImageIds.current.length > 0) {
                await Promise.all(
                    deletedImageIds.current.map(imageId =>
                        deleteCourseImage(personId, targetCourseId, imageId)
                    )
                );
            }

            // Clear refs
            pendingImageUploads.current = {};
            deletedImageIds.current = [];

            navigate(`/connect/console/${personId}`, { state: { tab: 'courses' } });
        } catch (err) {
            setError('Failed to save course');
            console.error(err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleBack = () => {
        const hasChanges = JSON.stringify(course) !== JSON.stringify(originalCourse);
        if (hasChanges && !isEditMode && (course.title || course.description || course.modules.length > 0)) {
            if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                return;
            }
        }
        navigate(`/connect/console/${personId}`, { state: { tab: 'courses' } });
    };

    // Toggle expansions
    const toggleModule = (moduleId) => {
        setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
    };

    const toggleChapter = (moduleId, chapterId) => {
        const key = `${moduleId}-${chapterId}`;
        setExpandedChapters(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleLesson = (moduleId, chapterId, lessonId) => {
        const key = `${moduleId}-${chapterId}-${lessonId}`;
        setExpandedLessons(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Module operations
    const addModule = () => {
        const newModule = {
            id: Date.now(),
            title: 'New Module',
            chapters: []
        };
        setCourse(prev => ({
            ...prev,
            modules: [...prev.modules, newModule]
        }));
        setExpandedModules(prev => ({ ...prev, [newModule.id]: true }));
    };

    const updateModule = (moduleId, updates) => {
        setCourse(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === moduleId ? { ...m, ...updates } : m)
        }));
    };

    const deleteModule = (moduleId) => {
        setCourse(prev => ({
            ...prev,
            modules: prev.modules.filter(m => m.id !== moduleId)
        }));
    };

    // Chapter operations
    const addChapter = (moduleId) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;

        const newChapter = {
            id: Date.now(),
            title: 'New Chapter',
            lessons: []
        };

        updateModule(moduleId, {
            chapters: [...(module.chapters || []), newChapter]
        });
        setExpandedChapters(prev => ({ ...prev, [`${moduleId}-${newChapter.id}`]: true }));
    };

    const updateChapter = (moduleId, chapterId, updates) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;

        updateModule(moduleId, {
            chapters: module.chapters.map(ch => ch.id === chapterId ? { ...ch, ...updates } : ch)
        });
    };

    const deleteChapter = (moduleId, chapterId) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;

        updateModule(moduleId, {
            chapters: module.chapters.filter(ch => ch.id !== chapterId)
        });
    };

    // Lesson operations
    const addLesson = (moduleId, chapterId) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;

        const newLesson = {
            id: Date.now(),
            title: 'New Lesson',
            contentBlocks: [{ id: `${Date.now()}`, type: 'text', value: '' }],
            videoLinks: []
        };

        updateChapter(moduleId, chapterId, {
            lessons: [...(chapter.lessons || []), newLesson]
        });
        setExpandedLessons(prev => ({ ...prev, [`${moduleId}-${chapterId}-${newLesson.id}`]: true }));
    };

    const updateLesson = (moduleId, chapterId, lessonId, updates) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;

        updateChapter(moduleId, chapterId, {
            lessons: chapter.lessons.map(l => l.id === lessonId ? { ...l, ...updates } : l)
        });
    };

    const deleteLesson = (moduleId, chapterId, lessonId) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;

        // Track deleted images for cleanup
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (lesson?.contentBlocks) {
            lesson.contentBlocks.forEach(block => {
                if (block.type === 'image' && block.url && !block._pendingFile) {
                    deletedImageIds.current.push(block.id);
                }
            });
        }

        updateChapter(moduleId, chapterId, {
            lessons: chapter.lessons.filter(l => l.id !== lessonId)
        });
    };

    // Content block operations
    const addContentBlock = (moduleId, chapterId, lessonId, type, afterBlockId = null) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        const blockId = `${Date.now()}`;
        const newBlock = {
            id: blockId,
            type: type,
            ...(type === 'text' ? { value: '' } : { url: null })
        };

        let newBlocks = [...(lesson.contentBlocks || [])];
        
        if (afterBlockId !== null) {
            const insertIndex = newBlocks.findIndex(b => b.id === afterBlockId) + 1;
            newBlocks.splice(insertIndex, 0, newBlock);
        } else {
            newBlocks.push(newBlock);
        }

        updateLesson(moduleId, chapterId, lessonId, { contentBlocks: newBlocks });
    };

    const updateContentBlock = (moduleId, chapterId, lessonId, blockId, updates) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        const newBlocks = (lesson.contentBlocks || []).map(block =>
            block.id === blockId ? { ...block, ...updates } : block
        );

        updateLesson(moduleId, chapterId, lessonId, { contentBlocks: newBlocks });
    };

    const deleteContentBlock = (moduleId, chapterId, lessonId, blockId) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        // Find the block to check if it's an image that needs cleanup
        const block = lesson.contentBlocks.find(b => b.id === blockId);
        if (block?.type === 'image') {
            // If it has a URL (already uploaded), mark for deletion
            if (block.url && !block._pendingFile) {
                deletedImageIds.current.push(block.id);
            }
            // If it has a pending file, remove from pending uploads
            if (block._pendingFile) {
                delete pendingImageUploads.current[block.id];
            }
        }

        const newBlocks = (lesson.contentBlocks || []).filter(block => block.id !== blockId);
        
        // Ensure at least one text block remains
        if (newBlocks.length === 0) {
            newBlocks.push({ id: `${Date.now()}`, type: 'text', value: '' });
        }

        updateLesson(moduleId, chapterId, lessonId, { contentBlocks: newBlocks });
    };

    const handleBlockImageUpload = (moduleId, chapterId, lessonId, blockId, file) => {
        if (!file || !file.type.startsWith('image/')) return;

        // Store file for upload on save
        pendingImageUploads.current[blockId] = file;

        // Create local preview URL
        const localPreview = URL.createObjectURL(file);

        updateContentBlock(moduleId, chapterId, lessonId, blockId, {
            _pendingFile: true,
            _localPreview: localPreview,
            url: null // Will be set after upload
        });
    };

    const replaceBlockImage = (moduleId, chapterId, lessonId, blockId, file) => {
        if (!file || !file.type.startsWith('image/')) return;

        // Get the current block to check if it has an existing URL
        const module = course.modules.find(m => m.id === moduleId);
        const chapter = module?.chapters.find(ch => ch.id === chapterId);
        const lesson = chapter?.lessons.find(l => l.id === lessonId);
        const block = lesson?.contentBlocks.find(b => b.id === blockId);

        // If replacing an already-uploaded image, mark old one for deletion
        if (block?.url && !block._pendingFile) {
            deletedImageIds.current.push(block.id);
        }

        // Store new file for upload
        pendingImageUploads.current[blockId] = file;

        // Create local preview
        const localPreview = URL.createObjectURL(file);

        updateContentBlock(moduleId, chapterId, lessonId, blockId, {
            _pendingFile: true,
            _localPreview: localPreview,
            url: null
        });
    };

    // Media handling
    const handleImageUpload = (moduleId, chapterId, lessonId, files) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        Array.from(files).forEach(file => {
            if (file.type.startsWith('image/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const newImages = [...(lesson.images || []), {
                        type: 'local',
                        data: e.target.result,
                        name: file.name
                    }];
                    updateLesson(moduleId, chapterId, lessonId, { images: newImages });
                };
                reader.readAsDataURL(file);
            }
        });
    };

    const handleVideoUpload = (moduleId, chapterId, lessonId, files) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        Array.from(files).forEach(file => {
            if (file.type.startsWith('video/')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const newVideos = [...(lesson.videoLinks || []), {
                        type: 'local',
                        data: e.target.result,
                        name: file.name
                    }];
                    updateLesson(moduleId, chapterId, lessonId, { videoLinks: newVideos });
                };
                reader.readAsDataURL(file);
            }
        });
    };

    const addVideoUrl = (moduleId, chapterId, lessonId) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        updateLesson(moduleId, chapterId, lessonId, {
            videoLinks: [...(lesson.videoLinks || []), { type: 'url', url: '' }]
        });
    };

    const updateVideoUrl = (moduleId, chapterId, lessonId, index, url) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        const newVideoLinks = [...lesson.videoLinks];
        newVideoLinks[index] = { type: 'url', url };
        updateLesson(moduleId, chapterId, lessonId, { videoLinks: newVideoLinks });
    };

    const removeMedia = (moduleId, chapterId, lessonId, type, index) => {
        const module = course.modules.find(m => m.id === moduleId);
        if (!module) return;
        const chapter = module.chapters.find(ch => ch.id === chapterId);
        if (!chapter) return;
        const lesson = chapter.lessons.find(l => l.id === lessonId);
        if (!lesson) return;

        if (type === 'image') {
            updateLesson(moduleId, chapterId, lessonId, {
                images: lesson.images.filter((_, i) => i !== index)
            });
        } else {
            updateLesson(moduleId, chapterId, lessonId, {
                videoLinks: lesson.videoLinks.filter((_, i) => i !== index)
            });
        }
    };

    if (isLoading) {
        return (
            <div className="course-editor">
                <div className="course-editor__loading">
                    <div className="loading-spinner"></div>
                    <p>Loading course...</p>
                </div>
            </div>
        );
    }

    if (error && isEditMode) {
        return (
            <div className="course-editor">
                <div className="course-editor__error">
                    <p>{error}</p>
                    <button onClick={handleBack}>Go Back</button>
                </div>
            </div>
        );
    }

    return (
        <div className="course-editor">
            {/* Header */}
            <header className="course-editor__header">
                <button className="course-editor__back" onClick={handleBack}>
                    <ArrowBackIcon />
                    <span>Back to Console</span>
                </button>
                <h1>{isEditMode ? 'Edit Course' : 'Create Course'}</h1>
                <button 
                    className="course-editor__save" 
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    {isSaving ? (
                        <>
                            <div className="save-spinner"></div>
                            Saving...
                        </>
                    ) : (
                        <>
                            <SaveIcon />
                            Save Course
                        </>
                    )}
                </button>
            </header>

            {/* Validation Error */}
            {validationError && (
                <div className="course-editor__validation-error">
                    {validationError}
                </div>
            )}

            {/* Course Details */}
            <div className="course-editor__content">
                <div className="course-editor__details">
                    <div className="course-editor__field">
                        <label>Course Title *</label>
                        <input
                            type="text"
                            value={course.title}
                            onChange={(e) => setCourse(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Enter course title"
                        />
                    </div>
                    <div className="course-editor__field">
                        <label>Course Description *</label>
                        <textarea
                            value={course.description}
                            onChange={(e) => setCourse(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Enter course description"
                            rows={3}
                        />
                    </div>
                </div>

                {/* Modules Section */}
                <div className="course-editor__modules">
                    <div className="course-editor__modules-header">
                        <h2><MenuBookIcon /> Modules ({course.modules?.length || 0})</h2>
                        <button className="course-add-btn" onClick={addModule}>
                            <AddIcon sx={{ fontSize: 16 }} /> Add Module
                        </button>
                    </div>

                    {course.modules.length === 0 ? (
                        <div className="course-editor__empty">
                            <p>No modules yet. Add your first module to get started.</p>
                        </div>
                    ) : (
                        <div className="course-editor__modules-list">
                            {course.modules.map((module, moduleIndex) => {
                                const isModuleExpanded = expandedModules[module.id];

                                return (
                                    <div key={module.id} className="course-module">
                                        <div className="course-module__header" onClick={() => toggleModule(module.id)}>
                                            <div className="course-module__toggle">
                                                {isModuleExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </div>
                                            <span className="course-module__number">{moduleIndex + 1}.</span>
                                            <input
                                                type="text"
                                                className="course-module__title-input"
                                                value={module.title}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    updateModule(module.id, { title: e.target.value });
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                placeholder="Module Title"
                                            />
                                            <div className="course-module__actions" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="course-action-btn course-action-btn--small course-action-btn--danger"
                                                    onClick={() => deleteModule(module.id)}
                                                    title="Delete Module"
                                                >
                                                    <DeleteIcon sx={{ fontSize: 14 }} />
                                                </button>
                                            </div>
                                        </div>

                                        {isModuleExpanded && (
                                            <div className="course-module__content">
                                                <div className="course-chapters-container">
                                                    <div className="course-section__header">
                                                        <span className="course-section__label">
                                                            <ArticleIcon sx={{ fontSize: 14 }} /> Chapters ({module.chapters?.length || 0})
                                                        </span>
                                                        <button className="course-add-btn course-add-btn--small" onClick={() => addChapter(module.id)}>
                                                            <AddIcon sx={{ fontSize: 12 }} /> Add Chapter
                                                        </button>
                                                    </div>

                                                    {(module.chapters || []).map((chapter, chapterIndex) => {
                                                        const chapterKey = `${module.id}-${chapter.id}`;
                                                        const isChapterExpanded = expandedChapters[chapterKey];

                                                        return (
                                                            <div key={chapter.id} className="course-chapter">
                                                                <div className="course-chapter__header" onClick={() => toggleChapter(module.id, chapter.id)}>
                                                                    <div className="course-chapter__toggle">
                                                                        {isChapterExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                                                    </div>
                                                                    <span className="course-chapter__number">{moduleIndex + 1}.{chapterIndex + 1}</span>
                                                                    <input
                                                                        type="text"
                                                                        className="course-chapter__input"
                                                                        value={chapter.title}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            updateChapter(module.id, chapter.id, { title: e.target.value });
                                                                        }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        placeholder="Chapter Title"
                                                                    />
                                                                    <div className="course-chapter__actions" onClick={(e) => e.stopPropagation()}>
                                                                        <button
                                                                            className="course-action-btn course-action-btn--tiny course-action-btn--danger"
                                                                            onClick={() => deleteChapter(module.id, chapter.id)}
                                                                        >
                                                                            <DeleteIcon sx={{ fontSize: 12 }} />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {isChapterExpanded && (
                                                                    <div className="course-chapter__content">
                                                                        <div className="course-lessons-container">
                                                                            <div className="course-lessons__header">
                                                                                <span className="course-lessons__label">
                                                                                    <PlayCircleOutlineIcon sx={{ fontSize: 14 }} /> Lessons ({chapter.lessons?.length || 0})
                                                                                </span>
                                                                                <button className="course-add-btn course-add-btn--tiny" onClick={() => addLesson(module.id, chapter.id)}>
                                                                                    <AddIcon sx={{ fontSize: 12 }} /> Add Lesson
                                                                                </button>
                                                                            </div>

                                                                            {(chapter.lessons || []).map((lesson, lessonIndex) => {
                                                                                const lessonKey = `${module.id}-${chapter.id}-${lesson.id}`;
                                                                                const isLessonExpanded = expandedLessons[lessonKey];

                                                                                return (
                                                                                    <div key={lesson.id} className="course-lesson">
                                                                                        <div className="course-lesson__header" onClick={() => toggleLesson(module.id, chapter.id, lesson.id)}>
                                                                                            <div className="course-lesson__toggle">
                                                                                                {isLessonExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                                                                            </div>
                                                                                            <span className="course-lesson__number">{moduleIndex + 1}.{chapterIndex + 1}.{lessonIndex + 1}</span>
                                                                                            <input
                                                                                                type="text"
                                                                                                className="course-lesson__title-input"
                                                                                                value={lesson.title || ''}
                                                                                                onChange={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    updateLesson(module.id, chapter.id, lesson.id, { title: e.target.value });
                                                                                                }}
                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                                placeholder="Lesson Title"
                                                                                            />
                                                                                            <div className="course-lesson__actions" onClick={(e) => e.stopPropagation()}>
                                                                                                <button
                                                                                                    className="course-action-btn course-action-btn--tiny course-action-btn--danger"
                                                                                                    onClick={() => deleteLesson(module.id, chapter.id, lesson.id)}
                                                                                                >
                                                                                                    <DeleteIcon sx={{ fontSize: 12 }} />
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>

                                                                                        {isLessonExpanded && (
                                                                                            <div className="course-lesson__content">
                                                                                                {/* Block-based content editor */}
                                                                                                <div className="course-lesson__field">
                                                                                                    <div className="course-lesson__field-header">
                                                                                                        <label>Lesson Content</label>
                                                                                                        <div className="course-lesson__upload-btns">
                                                                                                            <button 
                                                                                                                className="course-add-btn course-add-btn--tiny"
                                                                                                                onClick={() => addContentBlock(module.id, chapter.id, lesson.id, 'text')}
                                                                                                                title="Add text block"
                                                                                                            >
                                                                                                                <TextFieldsIcon sx={{ fontSize: 12 }} /> Text
                                                                                                            </button>
                                                                                                            <button 
                                                                                                                className="course-add-btn course-add-btn--tiny"
                                                                                                                onClick={() => addContentBlock(module.id, chapter.id, lesson.id, 'image')}
                                                                                                                title="Add image block"
                                                                                                            >
                                                                                                                <ImageIcon sx={{ fontSize: 12 }} /> Image
                                                                                                            </button>
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    <div className="content-blocks">
                                                                                                        {(lesson.contentBlocks || []).map((block, blockIndex) => {
                                                                                                            const blockKey = `${lessonKey}-block-${block.id}`;
                                                                                                            
                                                                                                            return (
                                                                                                                <div key={block.id} className="content-block">
                                                                                                                    <div className="content-block__header">
                                                                                                                        <span className="content-block__label">
                                                                                                                            {block.type === 'text' ? (
                                                                                                                                <><TextFieldsIcon sx={{ fontSize: 12 }} /> Text</>
                                                                                                                            ) : (
                                                                                                                                <><ImageIcon sx={{ fontSize: 12 }} /> Image</>
                                                                                                                            )}
                                                                                                                        </span>
                                                                                                                        <div className="content-block__actions">
                                                                                                                            <button
                                                                                                                                className="course-add-btn course-add-btn--tiny"
                                                                                                                                onClick={() => addContentBlock(module.id, chapter.id, lesson.id, 'text', block.id)}
                                                                                                                                title="Add text below"
                                                                                                                            >
                                                                                                                                <TextFieldsIcon sx={{ fontSize: 10 }} />
                                                                                                                            </button>
                                                                                                                            <button
                                                                                                                                className="course-add-btn course-add-btn--tiny"
                                                                                                                                onClick={() => addContentBlock(module.id, chapter.id, lesson.id, 'image', block.id)}
                                                                                                                                title="Add image below"
                                                                                                                            >
                                                                                                                                <ImageIcon sx={{ fontSize: 10 }} />
                                                                                                                            </button>
                                                                                                                            <button
                                                                                                                                className="course-action-btn course-action-btn--tiny course-action-btn--danger"
                                                                                                                                onClick={() => deleteContentBlock(module.id, chapter.id, lesson.id, block.id)}
                                                                                                                                title="Delete block"
                                                                                                                            >
                                                                                                                                <DeleteIcon sx={{ fontSize: 10 }} />
                                                                                                                            </button>
                                                                                                                        </div>
                                                                                                                    </div>

                                                                                                                    {block.type === 'text' ? (
                                                                                                                        <div className="content-block__richtext">
                                                                                                                            <div className="richtext-toolbar">
                                                                                                                                <button
                                                                                                                                    type="button"
                                                                                                                                    className="richtext-toolbar__btn"
                                                                                                                                    title="Bold"
                                                                                                                                    onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold', false, null); }}
                                                                                                                                >
                                                                                                                                    <strong>B</strong>
                                                                                                                                </button>
                                                                                                                                <button
                                                                                                                                    type="button"
                                                                                                                                    className="richtext-toolbar__btn"
                                                                                                                                    title="Bullet List"
                                                                                                                                    onMouseDown={(e) => { e.preventDefault(); document.execCommand('insertUnorderedList', false, null); }}
                                                                                                                                >
                                                                                                                                    •
                                                                                                                                </button>
                                                                                                                            </div>
                                                                                                                            <div
                                                                                                                                className="course-lesson__textarea content-block__textarea content-block__editable"
                                                                                                                                contentEditable
                                                                                                                                suppressContentEditableWarning
                                                                                                                                dangerouslySetInnerHTML={{ __html: block.value || '' }}
                                                                                                                                onBlur={(e) => updateContentBlock(module.id, chapter.id, lesson.id, block.id, { value: e.currentTarget.innerHTML })}
                                                                                                                                data-placeholder="Write your content here..."
                                                                                                                            />
                                                                                                                        </div>
                                                                                                                    ) : (
                                                                                                                        <div className="content-block__image-area">
                                                                                                                            <input
                                                                                                                                type="file"
                                                                                                                                ref={el => blockImageInputRefs.current[blockKey] = el}
                                                                                                                                accept="image/jpeg,image/png,image/webp"
                                                                                                                                style={{ display: 'none' }}
                                                                                                                                onChange={(e) => {
                                                                                                                                    if (e.target.files?.[0]) {
                                                                                                                                        if (block.url || block._pendingFile) {
                                                                                                                                            replaceBlockImage(module.id, chapter.id, lesson.id, block.id, e.target.files[0]);
                                                                                                                                        } else {
                                                                                                                                            handleBlockImageUpload(module.id, chapter.id, lesson.id, block.id, e.target.files[0]);
                                                                                                                                        }
                                                                                                                                    }
                                                                                                                                }}
                                                                                                                            />
                                                                                                                            
                                                                                                                            {(block.url || block._localPreview) ? (
                                                                                                                                <div className="content-block__image-preview">
                                                                                                                                    <img 
                                                                                                                                        src={block._localPreview || block.url} 
                                                                                                                                        alt="Content image"
                                                                                                                                        className="content-block__image"
                                                                                                                                    />
                                                                                                                                    <div className="content-block__image-overlay">
                                                                                                                                        <button
                                                                                                                                            className="course-add-btn course-add-btn--tiny"
                                                                                                                                            onClick={() => blockImageInputRefs.current[blockKey]?.click()}
                                                                                                                                            title="Replace image"
                                                                                                                                        >
                                                                                                                                            <SyncAltIcon sx={{ fontSize: 12 }} /> Replace
                                                                                                                                        </button>
                                                                                                                                    </div>
                                                                                                                                    {block._pendingFile && (
                                                                                                                                        <span className="content-block__image-pending">Pending upload</span>
                                                                                                                                    )}
                                                                                                                                </div>
                                                                                                                            ) : (
                                                                                                                                <div 
                                                                                                                                    className="content-block__image-upload"
                                                                                                                                    onClick={() => blockImageInputRefs.current[blockKey]?.click()}
                                                                                                                                >
                                                                                                                                    <ImageIcon sx={{ fontSize: 24 }} />
                                                                                                                                    <span>Click to upload image</span>
                                                                                                                                </div>
                                                                                                                            )}
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            );
                                                                                                        })}
                                                                                                        
                                                                                                        {(!lesson.contentBlocks || lesson.contentBlocks.length === 0) && (
                                                                                                            <div className="content-blocks__empty">
                                                                                                                <p>No content blocks. Add text or images above.</p>
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>

                                                                                                {/* Videos Section */}
                                                                                                <div className="course-lesson__field">
                                                                                                    <div className="course-lesson__field-header">
                                                                                                        <label><VideoLibraryIcon sx={{ fontSize: 14 }} /> Videos</label>
                                                                                                        <div className="course-lesson__upload-btns">
                                                                                                            <input
                                                                                                                type="file"
                                                                                                                ref={el => videoInputRefs.current[lessonKey] = el}
                                                                                                                accept="video/mp4,video/mov,video/webm"
                                                                                                                style={{ display: 'none' }}
                                                                                                                onChange={(e) => handleVideoUpload(module.id, chapter.id, lesson.id, e.target.files)}
                                                                                                            />
                                                                                                            <button 
                                                                                                                className="course-add-btn course-add-btn--tiny"
                                                                                                                onClick={() => videoInputRefs.current[lessonKey]?.click()}
                                                                                                            >
                                                                                                                <AddIcon sx={{ fontSize: 12 }} /> Upload
                                                                                                            </button>
                                                                                                            <button 
                                                                                                                className="course-add-btn course-add-btn--tiny"
                                                                                                                onClick={() => addVideoUrl(module.id, chapter.id, lesson.id)}
                                                                                                            >
                                                                                                                <AddIcon sx={{ fontSize: 12 }} /> Add URL
                                                                                                            </button>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    {(lesson.videoLinks || []).length > 0 && (
                                                                                                        <div className="course-lesson__video-list">
                                                                                                            {lesson.videoLinks.map((video, idx) => (
                                                                                                                <div key={idx} className="course-lesson__video-item">
                                                                                                                    {video.type === 'local' ? (
                                                                                                                        <div className="course-lesson__video-local">
                                                                                                                            <VideoLibraryIcon sx={{ fontSize: 16 }} />
                                                                                                                            <span className="course-lesson__video-name">{video.name}</span>
                                                                                                                        </div>
                                                                                                                    ) : (
                                                                                                                        <input
                                                                                                                            type="text"
                                                                                                                            className="course-lesson__media-input"
                                                                                                                            value={video.url || ''}
                                                                                                                            onChange={(e) => updateVideoUrl(module.id, chapter.id, lesson.id, idx, e.target.value)}
                                                                                                                            placeholder="Enter video URL"
                                                                                                                        />
                                                                                                                    )}
                                                                                                                    <button
                                                                                                                        className="course-action-btn course-action-btn--tiny course-action-btn--danger"
                                                                                                                        onClick={() => removeMedia(module.id, chapter.id, lesson.id, 'video', idx)}
                                                                                                                    >
                                                                                                                        <CloseIcon sx={{ fontSize: 12 }} />
                                                                                                                    </button>
                                                                                                                </div>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}

                                                                            {(chapter.lessons || []).length === 0 && (
                                                                                <p className="course-empty-text">No lessons in this chapter</p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}

                                                    {(module.chapters || []).length === 0 && (
                                                        <p className="course-empty-text">No chapters in this module</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CourseEditor;
